import { PropertySearchRequest, PropertySearchResponse, SearchOptionsResponse } from '../types/search';
import { API_BASE_URL } from '../config/api';
import { fetchWithAuth } from './httpClient';

export interface SearchFilters {
  province_code?: string;
  ward_code?: string;
  street?: string;
  search_query?: string;
  property_types?: string[];
  min_duration?: string;
  max_duration?: string;
  available_from?: string;
  min_price?: number;
  max_price?: number;
  min_area?: number;
  max_area?: number;
  min_bedrooms?: number;
  max_bedrooms?: number;
  min_bathrooms?: number;
  max_bathrooms?: number;
  page?: number;
  limit?: number;
  sort_by?: string;
  sort_order?: 'asc' | 'desc';
}

class SearchService {
  private async makeRequest<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const url = `${API_BASE_URL}${endpoint}`;
    
    const response = await fetchWithAuth(url, {
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      ...options,
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Search API error:', response.status, errorText);
      throw new Error(`Search API error: ${response.status} ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * Get search options (rental types, durations, etc.)
   */
  async getSearchOptions(): Promise<SearchOptionsResponse> {
    return this.makeRequest<SearchOptionsResponse>('/search/options');
  }

  /**
   * Search properties with filters
   */
  async searchProperties(
    filters: SearchFilters,
    opts?: { signal?: AbortSignal }
  ): Promise<PropertySearchResponse> {
    const searchRequest: PropertySearchRequest = {
      province_code: filters.province_code,
      ward_code: filters.ward_code,
      street: filters.street,
      search_query: filters.search_query,
      property_types: filters.property_types,
      min_duration: filters.min_duration,
      max_duration: filters.max_duration,
      available_from: filters.available_from || undefined,
      min_price: filters.min_price,
      max_price: filters.max_price,
      min_area: filters.min_area,
      max_area: filters.max_area,
      min_bedrooms: filters.min_bedrooms,
      max_bedrooms: filters.max_bedrooms,
      min_bathrooms: filters.min_bathrooms,
      max_bathrooms: filters.max_bathrooms,
      page: filters.page || 1,
      limit: filters.limit || 20,
      sort_by: filters.sort_by || 'created_at',
      sort_order: filters.sort_order || 'desc',
    };

    return this.makeRequest<PropertySearchResponse>('/search/properties', {
      method: 'POST',
      body: JSON.stringify(searchRequest),
      signal: opts?.signal,
    });
  }

  /**
   * Quick search for properties
   */
  async quickSearch(params: {
    search_query?: string;
    property_type?: string;
    province_code?: string;
    limit?: number;
  }): Promise<PropertySearchResponse> {
    const searchParams = new URLSearchParams();
    
    if (params.search_query) searchParams.append('search_query', params.search_query);
    if (params.property_type) searchParams.append('property_type', params.property_type);
    if (params.province_code) searchParams.append('province_code', params.province_code);
    if (params.limit) searchParams.append('limit', params.limit.toString());

    return this.makeRequest<PropertySearchResponse>(`/search/properties?${searchParams.toString()}`);
  }
}

export const searchService = new SearchService();
export default searchService;